import { Box, Flex, Text } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { AuthenticatedComponent } from '../auth/keycloak';
// import { SearchInput } from '../component/gameComponents/SearchInput';
import { TransactionListItem } from '../component/gameComponents/TransactionListItem';
import { PendingBalance } from '../component/storeComponents/PendingBalance';
import { PendingPayments } from '../component/storeComponents/PendingPayments';
import { CommonScrollList } from '../component/ScrollList';
import PageDataContext from '../context/PageDataContext';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { getTransactions, TransactionRecord } from '../service/analytics';
import { PaginatedData } from '../service/util';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';

const LogPrefix = 'DashboardTransactions';

export const DashboardTransactionsPage: AuthenticatedComponent = ({ keycloak }) => {
    const { isLoadFromCache } = useContext(PageDataContext);

    const [allItems, setItems] = usePageDataCache<{ [date: string]: TransactionRecord[] }>(
        'allItems',
        {},
    );
    const [sortedDates, setSortedDates] = usePageDataCache<string[]>('sortedDates', []);
    const [pagination, setPagination] = usePageDataCache<PaginatedData | null>('pagination', null);
    const [hasMore, setHasMore] = usePageDataCache<boolean>('hasMore', false);
    const [hasError, setHasError] = usePageDataCache<boolean>('hasError', false);
    const [searchText, setSearchText] = usePageDataCache<string>('searchText', '');
    const [isFetchingData, setIsFetchingData] = useState<Array<boolean>>([]);
    const [abortController, setAbortController] = useState<AbortController>();

    const resetFilters = () => {
        setSearchText('');
        fetchFreshData('');
    };

    const fetchFreshData = async (searchText: string) => {
        abortController?.abort();
        setItems({});
        setPagination(null);
        setHasMore(false);

        fetchData(true, '', searchText);
    };

    const fetchMoreData = async () => {
        const nextPage = pagination?.next || '';
        fetchData(false, nextPage, searchText);
    };

    const fetchData = async (reset: boolean, nextPage: string, searchTerm: string) => {
        const logPrefix = `[${LogPrefix}.fetchData]`;

        const userID = keycloak.subject;

        if (isFetchingData.length > 0 && !reset) {
            console.log(`${logPrefix} already fetching, skip request`);
            return;
        }
        isFetchingData.push(true);
        setIsFetchingData([...isFetchingData]);

        const newController = new AbortController();
        setAbortController(newController);

        console.log(`${logPrefix} starting with reset: ${reset}`);

        try {
            let sort_order = 'desc';

            // query sessions from lobby
            const transacs = await getTransactions(
                userID,
                searchTerm,
                searchTerm,
                sort_order,
                nextPage,
                keycloak.token,
            );

            console.log(`${logPrefix} receive: ${JSON.stringify(transacs)}`);

            setHasMore(transacs?.page.next !== undefined && transacs?.page.next !== '');
            setPagination(transacs?.page || null);

            let items = allItems;
            if (reset) {
                items = {};
            }

            const toAdd = transacs?.data || [];
            toAdd.forEach((x) => {
                const thisDate = new Date(x.timestamp_ms);
                const dateString = thisDate.toISOString().split('T')[0];
                let list = items[dateString];
                if (!list) {
                    list = [];
                    items[dateString] = list;
                }

                if (!x.record_id || !list.find((y) => y.record_id === x.record_id)) {
                    list.push(x);
                }
            });
            setItems({ ...items });

            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(false);
            console.log(`${logPrefix} done with reset: ${reset}`);
        } catch (ex) {
            isFetchingData.pop();
            setIsFetchingData([...isFetchingData]);
            setHasError(true);
            console.error(`${logPrefix} ${ex}`);
        }
    };

    const hasMoreData = (hasMore: boolean): boolean => {
        const logPrefix = `[${LogPrefix}.hasMoreData]`;
        console.log(`${logPrefix} hasMore: ${hasMore} isFetchingData: ${isFetchingData.length}`);
        return hasMore || isFetchingData.length > 0;
    };

    const renderContent = () => (
        <Flex id={'transac-scroll'} className={'dashboard-container'}>
            <Flex className={'dashboard-header'}>
                <Text>PENDING</Text>
            </Flex>
            <Flex direction={'column'}>
                <PendingBalance keycloak={keycloak} />
                <PendingPayments keycloak={keycloak} />
            </Flex>
            <Flex className={'dashboard-header'}>
                <Text>HISTORY</Text>
            </Flex>
            {sortedDates.length === 0 ? (
                <Text className={'no-transaction-data'}>No transaction history.</Text>
            ) : (
                <CommonScrollList
                    loading={isFetchingData.length > 0}
                    onLoadMore={fetchMoreData}
                    hasMore={hasMoreData(hasMore)}
                    hasError={hasError}
                    onRefresh={async () => {
                        await fetchFreshData(searchText);
                    }}
                    children={sortedDates.map((date, idx) => {
                        const items = allItems[date];
                        return (
                            <TransactionListItem
                                datas={items && [...items]}
                                index={idx}
                                date={date}
                                key={idx}
                            />
                        );
                    })}
                />
            )}
        </Flex>
    );

    useEffect(() => {
        // order by date descending
        setSortedDates(
            Array.from(Object.keys(allItems)).sort((n1, n2) => {
                return n1 > n2 ? -1 : 1;
            }),
        );
    }, [allItems]);

    useEffect(() => {
        if (!isLoadFromCache) {
            fetchFreshData(searchText);
        }
    }, []);

    return (
        <>
            <BrowserView>
                <Box>{renderContent()}</Box>
            </BrowserView>
            <MobileViewPage
                title="TRANSACTIONS"
                titleColor="#FFF"
                titleBackgroundColor="#6867dd"
                isLoading={isFetchingData.length > 0}
            >
                {/* <Flex className="tool-bar">
                    <SearchInput
                        view={'mobile'}
                        onSearchClear={resetFilters}
                        onTriggerSearch={fetchFreshData}
                    />
                </Flex> */}
                {renderContent()}
            </MobileViewPage>
        </>
    );
};
